/**
 * ── useModuleAccess ──
 * Per-module access check. Wraps the entitlement system with
 * module registry awareness for tenant eligibility.
 *
 * Usage:
 *   const { hasAccess, entitlement, definition } = useModuleAccess("sponsorships");
 */

import { useMemo } from "react";
import { useEntitlementAccess, type EntitlementResult } from "@/hooks/useEntitlementAccess";
import { useIsAdmin } from "@/hooks/useUserRole";
import { getModuleDefinition, type ModuleDefinition, type TenantType } from "@/config/moduleRegistry";

export interface ModuleAccessResult {
  /** Whether the user can access this module right now */
  hasAccess: boolean;
  /** Full entitlement details */
  entitlement: EntitlementResult;
  /** Module definition from the registry */
  definition: ModuleDefinition | undefined;
  /** Whether this module is eligible for the current tenant */
  isEligible: boolean;
  /** The current tenant type */
  tenantType: TenantType;
}

export function useModuleAccess(moduleKey: string): ModuleAccessResult {
  const { isMasterAdmin, isAssociationAdmin } = useIsAdmin();
  const { check } = useEntitlementAccess();

  return useMemo(() => {
    const definition = getModuleDefinition(moduleKey);
    const tenantType: TenantType = isMasterAdmin
      ? "platform"
      : isAssociationAdmin
        ? "association"
        : "org";

    // Platform admins always have access
    if (isMasterAdmin) {
      return {
        hasAccess: true,
        entitlement: {
          hasAccess: true,
          accessState: "included" as const,
          behavior: "show" as const,
          moduleStatus: "active" as const,
          restrictedBy: null,
          availableAddOns: [],
          minPlan: undefined,
          isTemporary: false,
        },
        definition,
        isEligible: true,
        tenantType,
      };
    }

    // Check tenant eligibility
    const isEligible = definition
      ? definition.eligibleTenants.includes(tenantType)
      : false;

    if (!isEligible) {
      return {
        hasAccess: false,
        entitlement: {
          hasAccess: false,
          accessState: "hidden" as const,
          behavior: "hide" as const,
          moduleStatus: null,
          restrictedBy: null,
          availableAddOns: [],
          minPlan: undefined,
          isTemporary: false,
        },
        definition,
        isEligible: false,
        tenantType,
      };
    }

    // Core modules are always accessible for eligible tenants
    if (definition?.isCore) {
      return {
        hasAccess: true,
        entitlement: {
          hasAccess: true,
          accessState: "included" as const,
          behavior: "show" as const,
          moduleStatus: "active" as const,
          restrictedBy: null,
          availableAddOns: [],
          minPlan: undefined,
          isTemporary: false,
        },
        definition,
        isEligible: true,
        tenantType,
      };
    }

    // Delegate to entitlement system
    const entitlement = check(moduleKey);
    return {
      hasAccess: entitlement.hasAccess,
      entitlement,
      definition,
      isEligible,
      tenantType,
    };
  }, [moduleKey, isMasterAdmin, isAssociationAdmin, check]);
}
